import { FC, useEffect, useState } from "react";
import {
    Button,
    DocumentList,
    Space,
    Spin,
    Tooltip,
    SimpleCard,
} from "../../../design-system";
import type { DocumentItem } from "../../../design-system";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { PlainLightPanelless } from "survey-core/themes";
import "survey-core/survey-core.css";

import {
    Project,
    ProjectAnalysis,
    ProjectAnalysisStatus,
    ProjectType,
    allowedExtensions,
} from "../../../types/ProjectTypes";
import {
    getSurveySchema,
    submitAnalysis,
    updateAnalysis,
} from "../../../common/api/ProjectClient";
import { handleError } from "../../../common/ErrorHandling";
import { useUserState } from "../../../common/UserContext";
import { ProjectTypeFileSuggestions } from "./ModalProjectWizard";
import { ProjectViewState } from "../ProjectViewState";
import styles from "../ProjectView.module.less";

function getSubmitLabel(project: Project, analysis: ProjectAnalysis, surveyResponse: Record<string, any>): string {

    // Check if user selected a closing outcome in the survey
    switch (project.type) {
        case ProjectType.DEAL: {
            const outcome = surveyResponse.dealOutcome;
            if (outcome && outcome !== "not_yet") return "Close deal";
            break;
        }
        case ProjectType.RFX: {
            const outcome = surveyResponse.rfxOutcome;
            if (outcome && outcome !== "in_progress") return "Complete RFx";
            break;
        }
    }

    return "Generate playbook";
}

interface InputEditorProps {
    state: ProjectViewState;
    readOnly?: boolean;
    onSaveDraft: () => void;
    onSubmit?: () => Promise<void>;
}

/**
 * Editable input form.
 * 
 * Always fetches the latest survey schema.
 * 
 * If the analysis is in draft, pre-fills documents and survey data from the draft input.
 * Otherwise shows project documents for a new submission so the user knows whether to add/remove documents.
 */
export const InputEditor: FC<InputEditorProps> = ({ state, readOnly, onSaveDraft, onSubmit }) => {
    const { me } = useUserState();
    const { project, analysis } = state;

    // load data from saved draft
    // in practice only the intake form can be in draft mode but we may have
    // older analysis in production still in draft and version n > 1
    const draftInput = analysis.status === ProjectAnalysisStatus.DRAFT
        ? analysis.inputs?.[0]
        : undefined;
    const draftSurvey = draftInput?.survey as { schema?: object; response?: object } | undefined;
    const draftResponse = draftSurvey?.response;

    // flag to show hints for intake (version = 1) and allow saving as draft
    const isIntake = analysis.version === 1 && analysis.status === ProjectAnalysisStatus.DRAFT;

    // List of existing documents to show the user
    const initialDocuments: DocumentItem[] = draftInput
        ? draftInput.documents.map((d) => ({
            id: d.id,
            name: d.document.name,
            uploadedBy: `${draftInput.creator.firstName} ${draftInput.creator.lastName}`,
            uploadedAt: d.document.uploadDate,
        }))
        : (project.documents?.map((d) => ({
            id: d.id,
            name: d.document.name,
            uploadedBy: `${d.creator.firstName} ${d.creator.lastName}`,
            uploadedAt: d.document.uploadDate,
        })) || []);

    const [surveyModel, setSurveyModel] = useState<Model | null>(null);
    const [schema, setSchema] = useState<object | undefined>(undefined);
    const [isLoadingSchema, setIsLoadingSchema] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [surveyResponse, setSurveyResponse] = useState<object>(draftResponse || {});
    const [documents, setDocuments] = useState<DocumentItem[]>(initialDocuments);

    useEffect(() => {
        const fetchSchema = async () => {
            try {
                setIsLoadingSchema(true);
                const { schema: surveySchema } = await getSurveySchema(project.id);
                setSchema(surveySchema);

                const model = new Model(surveySchema);
                model.applyTheme(PlainLightPanelless);
                model.showCompleteButton = false;
                model.textUpdateMode = "onTyping";

                if (draftResponse) {
                    model.data = draftResponse;
                }

                if (readOnly) {
                    model.mode = "display";
                }

                model.onValueChanged.add((survey) => {
                    setSurveyResponse(survey.data);
                });

                setSurveyModel(model);
            } catch (error) {
                handleError(error);
            } finally {
                setIsLoadingSchema(false);
            }
        };
        fetchSchema();
    }, [analysis.id]);

    const buildPayload = () => ({
        survey: { schema: schema!, response: surveyResponse },
        removedDocumentIds: documents
            .filter((d) => d.change === "removed")
            .map((d) => d.id as number),
        files: documents
            .filter((d) => d.change === "added" && d.file)
            .map((d) => d.file!),
    });

    const handleSaveDraft = async () => {
        if (!schema) return;
        try {
            setIsSaving(true);
            await updateAnalysis(project.id, buildPayload());
            onSaveDraft();
        } catch (error) {
            handleError(error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSubmit = async () => {
        if (!schema) return;
        try {
            setIsSubmitting(true);
            await submitAnalysis(project.id, buildPayload());
            await onSubmit?.();
        } catch (error) {
            handleError(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getSubmitDisabledReason = (): string | undefined => {
        if (!schema || !surveyModel) return "Survey not loaded";

        const validationModel = new Model(schema);
        validationModel.data = surveyResponse;
        validationModel.validate(false, false, undefined, undefined);
        if (validationModel.hasErrors()) return "Please complete the required fields";

        const activeCount = documents.filter((d) => d.change !== "removed").length;
        if (activeCount === 0) return "Please upload at least one document";

        const response = surveyResponse as Record<string, any>;
        if (response.dealOutcome === "signed") {
            const hasAddedFile = documents.some((d) => d.change === "added");
            if (!hasAddedFile) return "Please upload the signed contract";
        }

        return undefined;
    };

    if (isLoadingSchema) {
        return (
            <div style={{ textAlign: "center", padding: 48 }}>
                <Spin />
            </div>
        );
    }

    const submitDisabledReason = getSubmitDisabledReason();

    return (
        <Space direction="vertical" size="large" fullWidth>
            <SimpleCard header="Documents" headerType="primary">
                <DocumentList
                    key={documents.map((d) => d.id).join(",")}
                    documents={documents}
                    allowedExtensions={allowedExtensions}
                    readOnly={readOnly}
                    uploaderName={`${me.firstName} ${me.lastName}`}
                    onChange={setDocuments}
                />
                {isIntake && (
                    <div style={{ margin: "16px 0" }}>
                        {ProjectTypeFileSuggestions[project.type]}
                    </div>
                )}
            </SimpleCard>

            <SimpleCard header="Context" headerType="primary">
                <SimpleCard type="white">
                    {isIntake && (
                        <p>
                            This information helps tailor the analysis and negotiation
                            recommendations.
                        </p>
                    )}
                    <div className={styles.surveyContainer}>
                        {surveyModel && <Survey model={surveyModel} />}
                    </div>
                </SimpleCard>
            </SimpleCard>

            {!readOnly && (
                <div className={styles.submitBar}>
                    {isIntake && (
                        <Button
                            size="large"
                            onClick={handleSaveDraft}
                            loading={isSaving}
                            disabled={isSubmitting}
                        >
                            Save as draft
                        </Button>
                    )}
                    <Tooltip title={submitDisabledReason}>
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={!!submitDisabledReason || isSaving}
                        >
                            {getSubmitLabel(project, analysis, surveyResponse as Record<string, any>)}
                        </Button>
                    </Tooltip>
                </div>
            )}
        </Space>
    );
};
